import { useState, useCallback } from 'react';
import { Node, Edge } from 'reactflow';
import { WorkflowContextMenuManager } from '../WorkflowContextMenuManager';

export function useWorkflowContextMenu(
  contextMenuManager: WorkflowContextMenuManager,
  closeContextMenu: () => void
) {
  // State for context menu
  const [contextMenu, setContextMenu] = useState<{
    type: 'node' | 'edge' | 'canvas';
    x: number;
    y: number;
    id?: string;
    position?: { x: number, y: number };
    items: any[];
  } | null>(null);

  // Handle node context menu
  const onNodeContextMenu = useCallback((event: React.MouseEvent, node: Node) => {
    // Prevent default browser context menu
    event.preventDefault();
    event.stopPropagation();

    console.log(`Node Context Menu: Event triggered at clientX=${event.clientX}, clientY=${event.clientY}`);
    console.log(`Node Context Menu: Node ID=${node.id}`);

    const menuItems = contextMenuManager.getNodeContextMenu(node, closeContextMenu);
    
    // Set context menu state with coordinates
    setContextMenu({
      type: 'node',
      x: event.clientX,
      y: event.clientY,
      id: node.id,
      items: menuItems
    });
    
    // Force a re-render to ensure the context menu appears
    setTimeout(() => {
      console.log("Forcing re-render after context menu event");
    }, 0);
  }, [contextMenuManager, closeContextMenu]);

  // Handle edge context menu
  const onEdgeContextMenu = useCallback((event: React.MouseEvent, edge: Edge) => {
    // Prevent default browser context menu
    event.preventDefault();
    event.stopPropagation();

    console.log(`Edge Context Menu: Event triggered at clientX=${event.clientX}, clientY=${event.clientY}`);
    console.log(`Edge Context Menu: Edge ID=${edge.id}`);

    const menuItems = contextMenuManager.getEdgeContextMenu(edge, closeContextMenu);
    
    setContextMenu({
      type: 'edge',
      x: event.clientX,
      y: event.clientY,
      id: edge.id,
      items: menuItems
    });
  }, [contextMenuManager, closeContextMenu]);

  // Handle canvas context menu
  const onPaneContextMenu = useCallback((event: React.MouseEvent, reactFlowInstance: any, reactFlowWrapper: React.RefObject<HTMLDivElement>) => {
    // Prevent default browser context menu
    event.preventDefault();
    event.stopPropagation();

    // Get the exact position of the click
    const clientX = event.clientX;
    const clientY = event.clientY;
    
    console.log(`Canvas Context Menu: Event triggered at clientX=${clientX}, clientY=${clientY}`);
    
    // Get the ReactFlow container's bounding rectangle
    const reactFlowBounds = reactFlowWrapper.current?.getBoundingClientRect();
    console.log(`Canvas Context Menu: ReactFlow bounds top=${reactFlowBounds?.top}, left=${reactFlowBounds?.left}`);

    let position;
    if (reactFlowInstance) {
      // Use screenToFlowPosition to convert screen coordinates to flow coordinates
      position = reactFlowInstance.screenToFlowPosition({
        x: clientX,
        y: clientY
      });
      console.log(`Canvas Context Menu: Flow position x=${position.x}, y=${position.y}`);
    } else {
      console.log(`Canvas Context Menu: ReactFlow instance not available, using raw coordinates`);
      position = { x: clientX, y: clientY };
    }

    const menuItems = contextMenuManager.getCanvasContextMenu(position, closeContextMenu);
    
    // Set context menu with exact client coordinates
    setContextMenu({
      type: 'canvas',
      x: clientX,
      y: clientY,
      position,
      items: menuItems
    });
  }, [contextMenuManager, closeContextMenu]);
  
  return {
    contextMenu,
    setContextMenu,
    onNodeContextMenu,
    onEdgeContextMenu,
    onPaneContextMenu
  };
}
